/**
 * useUserAuth Hook
 * 
 * Custom hook for handling user authentication via midway SSO.
 * Manages authentication flow and provides fallback for localhost development.
 */

import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useUserContext } from '../contexts/user-context';

interface MidwayTokenPayload {
  sub?: string;
  [key: string]: any;
}

const getLocalUserAlias = (): string => {
  // Check for environment variable first
  const envUser = process.env.REACT_APP_DEV_USER_ALIAS;
  if (envUser) return envUser;
  
  // Fallback to generic dev user
  return "Dev User";
};

export const useUserAuth = () => {
  const { state, dispatch } = useUserContext();

  useEffect(() => {
    const authenticateUser = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        if (location.hostname === "localhost") {
          // For localhost development, use fallback user
          const localUser = getLocalUserAlias();
          dispatch({ type: 'SET_USER_ALIAS', payload: localUser });
          return;
        }

        // Production midway SSO authentication
        const clientId = location.origin;
        const midwaySSOURL = `https://midway-auth.amazon.com/SSO?scope=openid&response_type=id_token&response_mode=query&nonce=nonce&sentry_handler_version=AmazonLambdaInterceptorAuthorizer-1.0&client_id=${clientId}%3A443&redirect_uri=${clientId}`;
        
        const response = await fetch(midwaySSOURL, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
        }

        const tokenText = await response.text();
        const decoded: MidwayTokenPayload = jwtDecode(tokenText);
        
        const userAlias = decoded?.sub || "Unknown User";
        dispatch({ type: 'SET_USER_ALIAS', payload: userAlias });

      } catch (error) {
        console.error('Authentication error:', error);
        dispatch({ 
          type: 'SET_ERROR', 
          payload: error instanceof Error ? error.message : 'Authentication failed'
        });
        
        // Fallback to a default user on error
        dispatch({ type: 'SET_USER_ALIAS', payload: 'User' });
      }
    };

    // Only authenticate if we don't already have a user alias
    if (!state.userAlias && !state.isLoading) {
      authenticateUser();
    }
  }, [dispatch, state.userAlias, state.isLoading]);

  return {
    userAlias: state.userAlias,
    isLoading: state.isLoading,
    error: state.error,
    clearError: () => dispatch({ type: 'CLEAR_ERROR' })
  };
};
